import {
  parseArray,
  parseBoolean,
  parseDate,
  parseNumber,
  parseString,
} from 'utils/parsingHelpers'

import { GeckoEntryBase } from './geckoEntry'
import {
  isVisualInspectionFieldType,
  VisualInspectionFieldType,
} from './geckoVisualInspectionField'
import { RolodexEntryJson, RolodexEntryType } from './rolodexEntryFields'

/**
 * Parse field response value based on field type
 * @param value The raw value from JSON
 * @param fieldType The field type to determine parsing method
 * @returns Properly typed value or undefined
 */
function parseFieldResponseValue(
  value: unknown,
  fieldType: VisualInspectionFieldType
): string | number | boolean | string[] | undefined {
  if (value === null || value === undefined) {
    return undefined
  }

  switch (fieldType) {
    case 'text':
    case 'date':
      return parseString(value)
    case 'number':
      return parseNumber(value)
    case 'boolean':
      return parseBoolean(value)
    case 'select':
      return parseString(value)
    case 'multi-select':
      return parseArray<string>(value)
    default:
      return parseString(value)
  }
}

/**
 * An entry which represents an individual field response within a visual inspection.
 * Contains the field configuration, response value, and associated notes.
 * Photos and action items are handled separately through Rolodex attachments and mapping tables.
 */
export class GeckoVisualInspectionFieldResponse extends GeckoEntryBase<
  {
    field_id: string
    label: string
    description: string
    field_type: VisualInspectionFieldType
    required: boolean
    photographable: boolean
    options?: string[]
    order: number
    value?: string | number | boolean | string[]
    response_date?: Date
    notes: string[]
  },
  {
    organization: string
    site: string
    unit: string
    [RolodexEntryType.VISUAL_INSPECTION]: string
    [RolodexEntryType.VISUAL_INSPECTION_FORM_RESPONSE]: string
    [RolodexEntryType.VISUAL_INSPECTION_SECTION_RESPONSE]: string
  },
  Record<string, never>
> {
  /**
   * A simple constructor, private so that a more descriptive factory method
   * (which can return undefined) is used instead
   * @param json The raw JSON to create an instance from
   * @private
   */
  private constructor(json: RolodexEntryJson) {
    const safeFieldType = isVisualInspectionFieldType(json.data.field_type)
      ? json.data.field_type
      : 'text'

    if (
      json.data.field_type &&
      !isVisualInspectionFieldType(json.data.field_type)
    ) {
      console.warn(
        `Unknown field_type "${json.data.field_type}", defaulting to 'text'`
      )
    }

    const options = parseArray<string>(json.data.options)
    const notes = parseArray<string>(json.data.notes) ?? []
    const parsedValue = parseFieldResponseValue(json.data.value, safeFieldType)

    super(
      json,
      {
        field_id: parseString(json.data.field_id) ?? '',
        label: parseString(json.data.label) ?? '',
        description: parseString(json.data.description) ?? '',
        field_type: safeFieldType,
        required: parseBoolean(json.data.required) ?? false,
        photographable: parseBoolean(json.data.photographable) ?? false,
        options,
        order: parseNumber(json.data.order) ?? 0,
        value: parsedValue,
        response_date: parseDate(json.data.response_date),
        notes,
      },
      {
        organization: parseString(json.tags.organization) ?? '',
        site: parseString(json.tags.site) ?? '',
        unit: parseString(json.tags.unit) ?? '',
        [RolodexEntryType.VISUAL_INSPECTION]:
          parseString(json.tags[RolodexEntryType.VISUAL_INSPECTION]) ?? '',
        [RolodexEntryType.VISUAL_INSPECTION_FORM_RESPONSE]:
          parseString(
            json.tags[RolodexEntryType.VISUAL_INSPECTION_FORM_RESPONSE]
          ) ?? '',
        [RolodexEntryType.VISUAL_INSPECTION_SECTION_RESPONSE]:
          parseString(
            json.tags[RolodexEntryType.VISUAL_INSPECTION_SECTION_RESPONSE]
          ) ?? '',
      },
      {}
    )
  }

  /**
   * A simple factory method which creates a visual inspection field response from raw JSON
   * @param json The raw JSON to create an instance from
   * @constructor
   */
  public static CreateFromJSON(json: RolodexEntryJson) {
    return new GeckoVisualInspectionFieldResponse(json)
  }
}
